import { useCallback, useEffect, useRef, useState } from 'react';

import { readPageEditorView, writePageEditorView } from '../utils/pageEditorView';
import { useSessionUserId } from '../utils/session';

/**
 * The page-layout editor's canvas settings — snap, grid size, rulers — for the
 * signed-in person, remembered in this browser per account
 * (utils/pageEditorView.js).
 *
 *   const { view, setView } = usePageEditorView();
 *   setView({ snap: false });   // a patch; the rest is kept
 *
 * Another account signing in on the tab re-reads its own; `setView` keeps one
 * identity for the hook's lifetime.
 */
export default function usePageEditorView() {
	const userId = useSessionUserId();
	const [view, setViewState] = useState(() => readPageEditorView(userId));
	const viewRef = useRef(view);
	viewRef.current = view;
	const userRef = useRef(userId);

	useEffect(() => {
		if (userRef.current === userId) return;
		userRef.current = userId;
		const next = readPageEditorView(userId);
		viewRef.current = next;
		setViewState(next);
	}, [userId]);

	const setView = useCallback((patch) => {
		const next = { ...viewRef.current, ...patch };
		viewRef.current = next;
		setViewState(next);
		writePageEditorView(next, userRef.current);
	}, []);

	return { view, setView };
}
